import { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Video, 
  QrCode, 
  Calendar, 
  Mail, 
  Users, 
  Ticket,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  PlayCircle
} from 'lucide-react';

export default function HelpCenter() {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const tutorials = [
    {
      id: 'jitsi-recording',
      icon: Video,
      title: 'Come Registrare Meeting Jitsi',
      color: 'bg-red-100 text-red-600',
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <h4 className="font-bold text-red-900 mb-3">📹 Registrazione Meeting Step-by-Step:</h4>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-semibold text-red-900">Avvia il Meeting</p>
                  <p className="text-sm text-red-800">Entra nella room Jitsi dalla pagina streaming evento</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-semibold text-red-900">Trova i Controlli</p>
                  <p className="text-sm text-red-800">Guarda la barra in basso con tutti i pulsanti (mute, video, etc.)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-semibold text-red-900">Click "⚫ REC"</p>
                  <p className="text-sm text-red-800">Cerca l'icona del pallino rosso, potrebbe essere nel menu "..." (tre puntini)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <p className="font-semibold text-red-900">Conferma "Start Recording"</p>
                  <p className="text-sm text-red-800">Popup chiede conferma, click "Start" o "Inizia Registrazione"</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <p className="font-semibold text-red-900">⏺️ Pallino Rosso Appare</p>
                  <p className="text-sm text-red-800">Quando vedi il pallino rosso lampeggiante = sta registrando!</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">6</div>
                <div>
                  <p className="font-semibold text-red-900">Stop Registrazione</p>
                  <p className="text-sm text-red-800">Click di nuovo icona REC → "Stop Recording"</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">7</div>
                <div>
                  <p className="font-semibold text-red-900">✅ File Salvato</p>
                  <p className="text-sm text-red-800">La registrazione viene salvata automaticamente. Trovala in Dashboard → Evento → Tab Registrazioni</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Nota:</strong> La registrazione parte solo se l'organizzatore (host) la avvia. 
              I partecipanti normali vedranno il pallino rosso ma non possono avviarla.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>💾 Dove Finisce la Registrazione?</strong> Jitsi salva su Dropbox 
              (serve account Dropbox collegato) o puoi configurare storage custom nelle impostazioni avanzate.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'qr-locandine',
      icon: QrCode,
      title: 'QR Code per Locandine',
      color: 'bg-purple-100 text-purple-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Ogni evento genera automaticamente un QR code per pre-registrazione da stampare su poster/locandine.
          </p>
          
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
            <h4 className="font-bold text-purple-900 mb-3">🎯 Come Usare:</h4>
            <ol className="list-decimal list-inside space-y-2 text-purple-800">
              <li><strong>Vai all'Evento</strong> → Dashboard → Il Tuo Evento</li>
              <li><strong>Tab "QR Locandina"</strong> in alto</li>
              <li><strong>Scarica PNG</strong> o <strong>Stampa</strong> direttamente</li>
              <li><strong>Inserisci su:</strong>
                <ul className="list-disc list-inside ml-6 mt-2">
                  <li>Poster A3/A4</li>
                  <li>Locandine da appendere</li>
                  <li>Instagram Stories/Facebook</li>
                  <li>Newsletter email</li>
                  <li>Biglietti da visita</li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>✅ Quando le persone scanneranno il QR:</strong> Andranno direttamente 
              alla pagina di acquisto biglietti del tuo evento. Zero friction!
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'calendar-reminders',
      icon: Calendar,
      title: 'Calendario e Reminder Automatici',
      color: 'bg-blue-100 text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h4 className="font-bold text-blue-900 mb-3">📅 Sistema Reminder Automatico:</h4>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-blue-900">Per i Partecipanti:</p>
                <ul className="list-disc list-inside text-sm text-blue-800 ml-4 mt-1">
                  <li>Click "Aggiungi al Calendario" dopo acquisto</li>
                  <li>Scegli: Google, Outlook, Apple, o download .ics</li>
                  <li>Reminder automatici 24h + 30min inclusi nel file</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-blue-900">Per l'Organizzatore:</p>
                <ul className="list-disc list-inside text-sm text-blue-800 ml-4 mt-1">
                  <li><strong>AUTOMATICO:</strong> Sistema invia email 24h e 30min prima</li>
                  <li><strong>MANUALE:</strong> Tab "Reminder" → Invio immediato</li>
                  <li>Email include: dettagli, link biglietto, streaming</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>🤖 Automazione Attiva:</strong> I reminder vengono inviati automaticamente 
              senza che tu debba fare nulla. Il sistema controlla ogni 5 minuti.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'invitations',
      icon: Mail,
      title: 'Inviti con Logo Personalizzato',
      color: 'bg-orange-100 text-orange-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Invia inviti professionali con il tuo logo automaticamente incluso.
          </p>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
            <h4 className="font-bold text-orange-900 mb-3">📧 Come Inviare Inviti:</h4>
            <ol className="list-decimal list-inside space-y-2 text-orange-800">
              <li>Evento → Tab "Inviti"</li>
              <li>Inserisci lista email (una per riga, o separate da virgola)</li>
              <li>Personalizza messaggio (opzionale)</li>
              <li>Click "Anteprima" per vedere email con logo</li>
              <li>Click "Invia Inviti"</li>
            </ol>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              <strong>🎨 Template Include:</strong> Logo tuo in header, gradient viola brandizzato, 
              pulsante CTA "Acquista Biglietto", responsive mobile. Professional!
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'streaming',
      icon: Video,
      title: 'Streaming Integrato Jitsi',
      color: 'bg-pink-100 text-pink-600',
      content: (
        <div className="space-y-4">
          <div className="bg-pink-50 border-2 border-pink-200 rounded-lg p-4">
            <h4 className="font-bold text-pink-900 mb-3">🎥 Attivazione Streaming:</h4>
            <ol className="list-decimal list-inside space-y-2 text-pink-800">
              <li>Crea Evento → Tipo: "Online" o "Ibrido"</li>
              <li>Spunta checkbox "Usa Streaming Integrato Jitsi"</li>
              <li>Sistema crea room automaticamente</li>
              <li>Partecipanti ricevono link nel biglietto</li>
              <li>Click link → Entrata diretta (zero setup)</li>
            </ol>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>✅ Vantaggi:</strong> Gratis, illimitato, nessuna app da scaricare, 
              funziona da browser, include chat, screen share, recording.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'ai-chat',
      icon: MessageCircle,
      title: 'Chat AI Assistente',
      color: 'bg-indigo-100 text-indigo-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Il pulsante chat viola in basso a destra è un assistente AI che conosce TUTTO di ZonePass.
          </p>

          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <h4 className="font-bold text-indigo-900 mb-3">🤖 Cosa Può Fare:</h4>
            <ul className="list-disc list-inside space-y-2 text-indigo-800">
              <li><strong>Spiegare funzionalità</strong> - "Come funziona check-in multi-zona?"</li>
              <li><strong>Risolvere problemi</strong> - "Non vedo il pulsante pubblica"</li>
              <li><strong>Guide passo-passo</strong> - "Come creo biglietti VIP?"</li>
              <li><strong>Best practices</strong> - "Consigli per torneo calcio"</li>
              <li><strong>Segnalare bug</strong> - Analizza e registra per team tecnico</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>💡 Pro Tip:</strong> L'AI è addestrata su tutta la documentazione ZonePass. 
              Chiedile qualsiasi cosa prima di cercare nella guida!
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              📚 Centro Assistenza ZonePass
            </h1>
            <p className="text-xl text-gray-600">
              Guide, tutorial e istruzioni per usare al meglio la piattaforma
            </p>
          </div>

          {/* Quick Help Card */}
          <Card className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-purple-900 mb-2">
                    💬 Hai una domanda veloce?
                  </h3>
                  <p className="text-purple-800">
                    Usa la <strong>Chat AI</strong> (pulsante viola in basso a destra) - 
                    risponde istantaneamente su qualsiasi funzionalità!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tutorial Accordion */}
          <div className="space-y-4">
            {tutorials.map((tutorial) => {
              const Icon = tutorial.icon;
              const isOpen = openSection === tutorial.id;
              
              return (
                <Card key={tutorial.id} className="overflow-hidden">
                  <CardHeader 
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleSection(tutorial.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 ${tutorial.color} rounded-lg flex items-center justify-center`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <CardTitle className="text-xl">{tutorial.title}</CardTitle>
                      </div>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </CardHeader>
                  
                  {isOpen && (
                    <CardContent className="pt-0">
                      {tutorial.content}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Contact Support */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>🆘 Serve Altro Aiuto?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Se non trovi risposta qui, contattaci:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>📧 Email: <a href="mailto:support@zonepass.io" className="text-purple-600 hover:underline">support@zonepass.io</a></li>
                <li>💬 Chat AI integrata nella piattaforma</li>
                <li>📱 Oppure usa il tab "Supporto" nell'evento</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
